import {
  ProjectPageGetProjectByIdDocument,
  RssAssessmentType,
  StageActionAvailability,
  StageAvailableActionFragment,
  useCreateAssessmentStageRssActionMutation,
} from 'generated/graphql';
import { FC, useState } from 'react';
import {
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import {
  rssAlreadyExistsTooltipContent,
  rssNotAvailableTooltipContent,
} from './helpers';
import SkillsSelfAssessmentActionForm, { SkillsSelfAssessmentActionFormValues } from './SkillsSelfAssessmentActionForm';

interface IAddSkillsSelfAssessmentAction {
  softSkillAssessmentAvailability?: StageAvailableActionFragment;
  technicalSkillAssessmentAvailability?: StageAvailableActionFragment;
  stageId: string;
  projectId: number;
  onClose: () => void;
}

const getTooltipNote = (
  assessmentType: 'Soft skill' | 'Technical skill',
  availability?: StageActionAvailability
) => {
  if (availability === StageActionAvailability.AlreadyExists)
    return rssAlreadyExistsTooltipContent(assessmentType);

  if (availability === StageActionAvailability.NotAvailable)
    return rssNotAvailableTooltipContent(assessmentType);

  return '';
};

const AddSkillsSelfAssessmentAction: FC<IAddSkillsSelfAssessmentAction> = ({
  softSkillAssessmentAvailability,
  technicalSkillAssessmentAvailability,
  stageId,
  projectId,
  onClose,
}) => {
  const { handleMsgType } = useNotification();
  const [submitting, setSubmitting] = useState(false);

  const [createRssAction] = useCreateAssessmentStageRssActionMutation({
    onError: () => {
      handleMsgType({
        type: TNotification.error,
        message:
          'Oops, an error occurred while creating the Skills self-assessment action!',
      });
      setSubmitting(false);
    },
    refetchQueries: [
      { query: ProjectPageGetProjectByIdDocument, variables: { id: projectId } },
    ],
  });

  const handleSubmit = async ({
    softSkillsChecked,
    technicalSkillsChecked,
  }: SkillsSelfAssessmentActionFormValues) => {
    const typesToCreate: RssAssessmentType[] = [];

    if (softSkillsChecked) {
      typesToCreate.push(RssAssessmentType.SoftSkill);
    }

    if (technicalSkillsChecked) {
      typesToCreate.push(RssAssessmentType.TechnicalSkill);
    }

    if (typesToCreate.length) {
      setSubmitting(true);
      try {
        for (const type of typesToCreate) {
          await createRssAction({
            variables: { stageId, type },
          });
        }
        handleMsgType({
          type: TNotification.success,
          message: 'Skills self-assessment action created!',
        });
        onClose();
      } catch {
        // Error handled by onError callback
      }
    }
  };

  const softSkillTooltipNote = getTooltipNote(
    'Soft skill',
    softSkillAssessmentAvailability?.availability
  );

  const technicalSkillTooltipNote = getTooltipNote(
    'Technical skill',
    technicalSkillAssessmentAvailability?.availability
  );

  return (
    <SkillsSelfAssessmentActionForm
      initialValues={{
        softSkillsChecked: false,
        technicalSkillsChecked: false,
      }}
      softSkillsAssessmentAvailable={
        softSkillAssessmentAvailability?.availability ===
        StageActionAvailability.Available
      }
      softSkillTooltipNote={softSkillTooltipNote}
      technicalSkillsAssessmentAvailable={
        technicalSkillAssessmentAvailability?.availability ===
        StageActionAvailability.Available
      }
      technicalSkillTooltipNote={technicalSkillTooltipNote}
      submitDisabled={submitting}
      submitButtonText="Add action"
      onCancel={onClose}
      onSubmit={handleSubmit}
    />
  );
};

export default AddSkillsSelfAssessmentAction;
